import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// @material-ui/core components
import withStyles from "@material-ui/core/styles/withStyles";
import Card from "~/material-dashboard/components/Card/Card.jsx";
import CardBody from "~/material-dashboard/components/Card/CardBody.jsx";
import Button from "~/material-dashboard/components/CustomButtons/Button.jsx";
import GridContainer from "~/material-dashboard/components/Grid/GridContainer.jsx";
import GridItem from "~/material-dashboard/components/Grid/GridItem.jsx";

import consultationStyles from "../styles/consultationStyles.js";

import imgSamplePackAllFlavors from "~assets/images/flavors/sample-pack-all-flavors.png";
import imgSpearmint from "~assets/images/flavors/spearmint-flavor.png";
import imgTangerine from "~assets/images/flavors/tangerine-flavor.png";
import imgFruitPunch from "~assets/images/flavors/tropical-fruit-medley-flavor.png";

import imgOnePack from "~assets/images/visits/one-pack.png";

import {
  getOrderItems,
  getVisitID,
  getVisitUSState,
  removeConsultationTypeOrderItemAddon,
  setConsultationTypeOrderItemAddon,
} from "~/visits/utils/visitCookies";

import {
  COLOR_KICK_BLUE,
  FRUIT_PUNCH_FLAVOR,
  ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS,
  ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
  PRICE_PROP_SAMPLE_PACK_ALL_FLAVORS,
  PRICE_PROP_TRIAL_PACK_FLAVORED,
  SPEARMINT_FLAVOR,
  TANGERINE_FLAVOR,
} from "~/shared/sharedConstants.js";

import { supportedUSStatesForCompoundedMeds } from "~/shared/utils/sharedUtils";

import { kickBlue } from "~/shared/styles/baseStyles";

const addonOptions = [
  {
    name: "Tropical Fruit Medley",
    image: imgFruitPunch,
    formFactor: "6-tablet sample pack",
    flavorConst: FRUIT_PUNCH_FLAVOR,
    orderType: ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
    priceDiff: PRICE_PROP_TRIAL_PACK_FLAVORED,
  },
  {
    name: "Spearmint Chill",
    image: imgSpearmint,
    formFactor: "6-tablet sample pack",
    flavorConst: SPEARMINT_FLAVOR,
    orderType: ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
    priceDiff: PRICE_PROP_TRIAL_PACK_FLAVORED,
  },
  {
    name: "Tangerine Citrus Burst",
    image: imgTangerine,
    formFactor: "6-tablet sample pack",
    flavorConst: TANGERINE_FLAVOR,
    orderType: ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
    priceDiff: PRICE_PROP_TRIAL_PACK_FLAVORED,
  },
  {
    name: "All Flavors Sample Pack",
    image: imgSamplePackAllFlavors,
    formFactor: "12-tablet sample pack (4 of each flavor)",
    flavorConst: "all-flavors",
    orderType: ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS,
    priceDiff: PRICE_PROP_SAMPLE_PACK_ALL_FLAVORS,
  },
  {
    name: "No Thanks. Traditional slow-acting tablets only",
    image: imgOnePack,
    flavorConst: "",
    orderType: "",
    priceDiff: 0,
  },
];
const SelectBBFlavoredTrial = ({ classes, history, match }) => {
  const consultationType = match.params.consultationType;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const checkoutSessionID = queryParams.get("checkoutSessionID");
  if (!checkoutSessionID) {
    window.location.href = "/me/portal";
  }

  const orderItems = getOrderItems(checkoutSessionID);
  const currentSelectedAddonFlavor =
    orderItems[consultationType]?.addons?.[ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED]
      ?.subcategory;
  const currentSelectedAllFlavors =
    orderItems[consultationType]?.addons?.[
      ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS
    ];

  const getInitialSelectedFlavor = () => {
    if (currentSelectedAllFlavors) {
      return addonOptions.find(
        (f) => f.orderType === ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS,
      );
    }
    if (currentSelectedAddonFlavor) {
      return addonOptions.find(
        (f) => f.flavorConst === currentSelectedAddonFlavor,
      );
    }
    return null;
  };

  const [selectedTrialFlavor, setSelectedTrialFlavor] = useState(
    getInitialSelectedFlavor(),
  );
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!getVisitID(checkoutSessionID)) {
      window.location.href = "/me/portal";
      return;
    }

    const visitIDCheckPoller = setInterval(() => {
      if (!getVisitID(checkoutSessionID)) {
        window.location.href = "/me/portal";
      }
    }, 1000);

    if (!supportedUSStatesForCompoundedMeds.includes(getVisitUSState())) {
      history.replace(
        `/visits/${consultationType}/payment/order-details?checkoutSessionID=${checkoutSessionID}`,
      );
    }

    return () => clearInterval(visitIDCheckPoller);
  }, []);

  const handleSelection = (flavor) => {
    setSelectedTrialFlavor(flavor);
    setShowError(false);
  };

  const nextQuestion = () => {
    if (selectedTrialFlavor === null) {
      setShowError(true);
      return;
    }

    // Remove any existing addon first
    removeConsultationTypeOrderItemAddon(
      consultationType,
      ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
      checkoutSessionID,
    );
    removeConsultationTypeOrderItemAddon(
      consultationType,
      ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS,
      checkoutSessionID,
    );

    // Flavored is selected
    if (selectedTrialFlavor && selectedTrialFlavor.flavorConst !== "") {
      setConsultationTypeOrderItemAddon(
        consultationType,
        selectedTrialFlavor.orderType,
        selectedTrialFlavor.flavorConst,
        checkoutSessionID,
      );
    }

    history.push(
      `/visits/${consultationType}/payment/order-details?checkoutSessionID=${checkoutSessionID}`,
    );
    window.scrollTo(0, 0);
  };

  const isSelected = (flavor) => {
    return (
      selectedTrialFlavor &&
      selectedTrialFlavor.flavorConst === flavor.flavorConst
    );
  };

  return (
    <div
      className={classes.container}
      style={{ backgroundColor: COLOR_KICK_BLUE }}
    >
      <GridContainer justifyContent="center">
        <GridItem xs={12} sm={10} md={6}>
          <h3
            style={{
              color: "#FFFFFF",
              fontFamily: "Spectral-SemiBold",
              fontSize: "32px",
              marginTop: "40px",
            }}
          >
            <strong>Limited Time Offer:</strong> Add A Sample Pack
          </h3>
          <p
            style={{
              color: "#FFFFFF",
              fontFamily: "BasisGrotesquePro-Regular",
              fontSize: "18px",
              lineHeight: "26px",
            }}
          >
            Try before you commit. Add any 6-tablet Sample Pack to your order
            for only $12 ($2/dose). <br />
            <br />
            Experience our premium fast-acting meds alongside traditional
            tablets and find your perfect match today.
            <br />
            <br />
          </p>
        </GridItem>
        <GridItem xs={12} sm={12} md={12}></GridItem>
        <GridItem xs={12} sm={10} md={6}>
          <Card
            className={classes.cardConsultation}
            style={{ paddingTop: "20px", paddingBottom: "20px" }}
          >
            <CardBody
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "0 2px",
              }}
            >
              {addonOptions.map((flavor, idx) => (
                <div key={flavor.flavorConst || "no-flavor"}>
                  <div
                    className={classes.selectFlavoredProductWrapper}
                    style={{
                      overflow: "hidden",
                      backgroundColor: isSelected(flavor) ? "#fff7e5" : "",
                    }}
                    onClick={() => handleSelection(flavor)}
                  >
                    <img
                      src={flavor.image}
                      style={{
                        position: "relative",
                        width: "200px",
                        height: "200px",
                      }}
                      alt={`${flavor.name} Flavor`}
                    />
                    <div className={classes.selectFlavorImageTitleWrapper}>
                      <h4
                        className={classes.selectFlavorImageTitle}
                        style={{
                          color: kickBlue,
                          fontWeight: "bold",
                        }}
                      >
                        {flavor.name}
                      </h4>
                      {flavor.formFactor && (
                        <p style={{ marginBottom: "0" }}>{flavor.formFactor}</p>
                      )}
                      {!!flavor.priceDiff && (
                        <p
                          style={{ marginBottom: "0" }}
                        >{`+$${flavor.priceDiff}`}</p>
                      )}
                    </div>
                  </div>
                  {idx !== addonOptions.length - 1 && (
                    <div
                      style={{
                        height: "1px",
                        margin: "0 25%",
                        backgroundColor: "#aaa",
                      }}
                    />
                  )}
                </div>
              ))}
            </CardBody>
          </Card>
          <div style={{ color: "red", margin: "20px auto 6px auto" }}>
            <p style={{ textAlign: "center" }}>
              {showError ? "Please select an option above" : ""}
            </p>
          </div>
          <Button
            onClick={() => {
              nextQuestion();
            }}
            style={{
              display: "block",
              backgroundColor: "#F25930",
              margin: "16px auto",
              width: "300px",
              fontSize: "18px",
              fontWeight: "medium",
              textTransform: "unset",
            }}
          >
            Continue
          </Button>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <a
              onClick={() => {
                history.push(
                  `/visits/${consultationType}/payment/order-details?checkoutSessionID=${checkoutSessionID}`,
                );
              }}
              style={{
                backgroundColor: "transparent",
                color: "white",
                fontSize: "18px",
                fontWeight: "normal",
                textAlign: "center",
                paddingBottom: "20px",
                cursor: "pointer",
              }}
            >
              Skip
            </a>
          </div>
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default withStyles(consultationStyles)(SelectBBFlavoredTrial);
